import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, Printer, Download } from 'lucide-react';
import api, { getProfileAssetUrl } from '../../utils/api';
import {
  buildIdCardHtmlPair,
  buildPrintSheetHtml,
  downloadPrintSheetPdf,
  printPrintSheet,
} from '../../utils/idCardExportService';
import { mergePrintSettings } from '../../utils/idCardPrintSettings';
import { computeSheetGrid } from '../../utils/idCardSheetLayout';
import { idCardImageCacheKey } from '../../utils/idCardCardData';
import { useToast } from '../../hooks/use-toast';

export default function IdCardSheetPreviewModal({
  open,
  onOpenChange,
  students = [],
  schoolId,
  format = 'portrait',
  design = 'saffron',
  printSettings = {},
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sheetHtml, setSheetHtml] = useState('');
  const [pairs, setPairs] = useState([]);
  const [error, setError] = useState('');

  const settings = useMemo(
    () => mergePrintSettings({ ...printSettings, cardFormat: format }),
    [printSettings, format],
  );

  const grid = useMemo(() => computeSheetGrid(settings), [settings]);
  const previewScale = useMemo(() => {
    const maxW = 720;
    const pagePx = (grid.pageWidthMm / 25.4) * 96;
    return Math.min(1, maxW / pagePx);
  }, [grid.pageWidthMm]);

  useEffect(() => {
    if (!open || !schoolId || !students.length) {
      setSheetHtml('');
      setPairs([]);
      setError('');
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    (async () => {
      try {
        const schoolRes = await api.get(`schools/${schoolId}`);
        const schoolData = schoolRes.data?.data ?? null;
        if (cancelled) return;

        const cacheKey = idCardImageCacheKey(schoolData);
        const built = await Promise.all(
          students.map(async (student) => {
            const photoUrl = getProfileAssetUrl(student.avatar);
            return buildIdCardHtmlPair(student, schoolData, photoUrl, { format, design, imageCacheKey: cacheKey });
          }),
        );

        if (cancelled) return;
        setPairs(built);
        const html = buildPrintSheetHtml(built, settings);
        setSheetHtml(html);
        if (!html) setError('Preview was empty');
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load sheet preview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, schoolId, students, format, design, settings]);

  const handlePrint = async () => {
    if (!pairs.length) return;
    try {
      await printPrintSheet(pairs, settings);
    } catch (e) {
      toast({ title: 'Print failed', description: e?.message || 'Could not print', variant: 'destructive' });
    }
  };

  const handleDownload = async () => {
    if (!pairs.length) return;
    setDownloading(true);
    try {
      const fn = await downloadPrintSheetPdf(pairs, settings);
      toast({ title: 'Downloaded', description: fn });
    } catch (e) {
      toast({ title: 'Download failed', description: e?.message || 'PDF error', variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] h-[90vh] flex flex-col">
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <DialogTitle>Print sheet preview</DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              {students.length} student{students.length !== 1 ? 's' : ''} · same layout as PDF download
            </p>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" disabled={!sheetHtml || downloading} onClick={handleDownload}>
              {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
              Download PDF
            </Button>
            <Button type="button" variant="outline" size="sm" disabled={!sheetHtml} onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />Print
            </Button>
          </div>
        </DialogHeader>
        <div className="flex-1 min-h-0 rounded-lg border overflow-auto bg-slate-100 dark:bg-slate-900/40 p-4">
          {loading ? (
            <div className="h-full min-h-[200px] flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading {students.length} cards…
            </div>
          ) : error ? (
            <div className="h-full min-h-[200px] flex items-center justify-center text-red-500 px-4 text-center">{error}</div>
          ) : sheetHtml ? (
            <div className="flex flex-col items-center gap-6">
              <iframe
                title="id-card-sheet-preview"
                className="bg-white shadow-lg border border-border"
                style={{
                  width: `${grid.pageWidthMm}mm`,
                  height: `${grid.pageHeightMm}mm`,
                  transform: `scale(${previewScale})`,
                  transformOrigin: 'top center',
                  marginBottom: `${-grid.pageHeightMm * (1 - previewScale) * (25.4 / 96)}px`,
                }}
                sandbox="allow-same-origin allow-modals"
                srcDoc={sheetHtml}
              />
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
